import axios, { AxiosInstance } from 'axios';
import * as React from 'react';

import { AuthContext, useAuth } from './AuthProvider';

export const ApiContext = React.createContext<AxiosInstance | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export const ApiProvider: React.FC<Props> = ({ children }) => {
  const [axiosInstance, setAxiosInstance] = React.useState<{ axios: AxiosInstance }>();
  const authInstance = React.useRef<AuthContext>();
  authInstance.current = useAuth();

  React.useEffect(() => {
    // Create Axios instance to manage API request logic
    const instance = axios.create({ baseURL: '/api' });
    // Attach Auth header when making API requests
    instance.interceptors.request.use(async (req) => {
      const accessToken = authInstance.current?.accessToken;
      if (accessToken) {
        req.headers.Authorization = `Bearer ${accessToken}`;
      }
      return req;
    });
    // We have to wrap the instance in an object because the create axios
    // instance is actually a function, which react state setter doesn't like
    setAxiosInstance({ axios: instance });
  }, []);

  if (!axiosInstance) {
    return null;
  }

  return (
    <ApiContext.Provider value={axiosInstance.axios}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): AxiosInstance => {
  const context = React.useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi cannot be called outside ApiProvider');
  }
  return context;
};
